"use client";

import {
  useEffect,
  useLayoutEffect,
  useRef,
  type RefObject,
} from "react";

// useLayoutEffect runs synchronously after DOM mutations but before
// paint. This matters because the activation step needs to snapshot
// `document.activeElement` BEFORE any child component's useEffect
// auto-focuses something inside the new overlay (e.g. ChatInput
// auto-focuses its textarea on mount). On the server, fall back to
// useEffect — the hook is no-op outside the browser anyway.
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * Trap keyboard focus inside a container while an overlay is open.
 *
 * Without this, tabbing inside ChatSheet or SettingsSheet flows out
 * to the background sidebar and the user "escapes" the modal without
 * realizing it. The audit explicitly flagged this as a WCAG 2.2 AA
 * regression.
 *
 * Behavior:
 *  - On activate: remembers the element that had focus and moves
 *    focus to `initialFocus()` (or the first focusable inside the
 *    container).
 *  - While active: Tab / Shift+Tab cycle within the container; Esc
 *    fires `onEscape` if provided.
 *  - On deactivate: restores focus to whichever element triggered
 *    the overlay so the user picks up where they left off.
 *
 * The hook is intentionally side-effect-only — it doesn't render
 * anything. Consumers wire `aria-modal`, `role="dialog"`, and
 * `aria-labelledby` themselves so semantics stay in JSX where they
 * read.
 */

interface FocusTrapOptions {
  /** Called when Esc is pressed. Hook listens for Esc only when this is set. */
  onEscape?: () => void;
  /** Override which element receives initial focus. Defaults to first focusable. */
  initialFocus?: () => HTMLElement | null;
  /** Restore focus to the element that had it before activation. Default true. */
  restoreFocus?: boolean;
}

const FOCUSABLE_SELECTORS = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "textarea:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
  ).filter((el) => {
    if (el.hasAttribute("aria-hidden")) return false;
    if (el.tabIndex < 0) return false;
    // offsetParent is null for elements that aren't in the layout
    // (display:none, detached). Position:fixed is the exception
    // because it's still rendered.
    if (el.offsetParent === null && getComputedStyle(el).position !== "fixed") {
      return false;
    }
    return true;
  });
}

function firstFocusable(container: HTMLElement): HTMLElement | null {
  return focusableElements(container)[0] ?? null;
}

export function useFocusTrap(
  active: boolean,
  containerRef: RefObject<HTMLElement | null>,
  options: FocusTrapOptions = {}
): void {
  const onEscapeRef = useRef(options.onEscape);
  const initialFocusRef = useRef(options.initialFocus);
  const restoreFocus = options.restoreFocus ?? true;

  // Keep callback identities live without re-running the activation
  // effect — important so consumers can pass inline arrows safely.
  useEffect(() => {
    onEscapeRef.current = options.onEscape;
    initialFocusRef.current = options.initialFocus;
  });

  useIsomorphicLayoutEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused =
      (document.activeElement as HTMLElement | null) ?? null;

    // Defer initial focus by one tick so React's commit settles. If
    // we focus synchronously the target may not yet be in the DOM
    // when state transitions from closed -> open in the same render.
    const focusTimer = window.setTimeout(() => {
      const target = initialFocusRef.current?.() ?? firstFocusable(container);
      target?.focus();
    }, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && onEscapeRef.current) {
        event.stopPropagation();
        onEscapeRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const focusables = focusableElements(container);
      if (focusables.length === 0) {
        // Empty trap — block Tab so focus can't escape, even though
        // there's nothing inside to land on.
        event.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const current = document.activeElement as HTMLElement | null;
      const inside = !!current && container.contains(current);

      if (event.shiftKey) {
        if (current === first || !inside) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (current === last || !inside) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown, true);
      if (
        restoreFocus &&
        previouslyFocused &&
        document.body.contains(previouslyFocused)
      ) {
        previouslyFocused.focus();
      }
    };
  }, [active, containerRef, restoreFocus]);
}
